/**
 * Portail Client : Documents à signer (centralisation). Affiche :
 *   1. Documents envoyés via client_documents
 *   2. Mandats Google Docs (signed_mandats)
 *
 * Pour chaque document à signer :
 *   - Aperçu du PDF dans une iframe
 *   - Canvas de signature en bas
 *   - Bouton "Valider la signature" → stamp PDF + verrou + notif admin
 *
 * Pour les documents déjà signés :
 *   - Badge vert "Signé le {date}"
 *   - Bouton "Télécharger le PDF signé"
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  FileSignature, CheckCircle2, Download, Loader2, Pen, X,
  Lock, ExternalLink,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { API, portalHeaders, secureDownload } from "../helpers.js";
import PdfFrame from "@/components/shared/PdfFrame";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CA", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

function StatusPill({ status }) {
  const map = {
    pending: { bg: "bg-amber-100", fg: "text-amber-700", label: "À signer", icon: <FileSignature className="w-3.5 h-3.5" /> },
    ready_to_sign: { bg: "bg-amber-100", fg: "text-amber-700", label: "À signer", icon: <FileSignature className="w-3.5 h-3.5" /> },
    signed: { bg: "bg-emerald-100", fg: "text-emerald-700", label: "Signé", icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
  };
  const s = map[status] || { bg: "bg-slate-100", fg: "text-slate-600", label: status || "—", icon: null };
  return (
    <span className={`inline-flex items-center gap-1 ${s.bg} ${s.fg} text-xs font-medium px-2 py-0.5 rounded-full`}>
      {s.icon}{s.label}
    </span>
  );
}

// ── Modale de signature pour les nouveaux documents (client_documents) ──
function DocumentSignModal({ document: doc, onClose, onSigned, primaryColor }) {
  const [hasContent, setHasContent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [consent, setConsent] = useState(false);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  // PDF URL avec token via param car iframe ne peut pas envoyer un Bearer header
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/portal/documents-to-sign/${doc.id}/pdf`, {
          headers: portalHeaders(),
          responseType: "blob",
        });
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        setPdfBlobUrl(url);
      } catch {
        if (!cancelled) toast.error("Impossible de charger le document à signer");
      }
    })();
    return () => { cancelled = true; if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc.id]);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = primaryColor;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
  }, [primaryColor]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const sx = c.width / rect.width;
    const sy = c.height / rect.height;
    const isTouch = e.touches && e.touches.length > 0;
    const x = (isTouch ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (isTouch ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x: x * sx, y: y * sy };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasContent(true);
  };
  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    setHasContent(false);
  };

  const submit = async () => {
    if (!hasContent) return;
    if (!consent) {
      toast.error("Veuillez confirmer votre consentement");
      return;
    }
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const r = await axios.post(
        `${API}/portal/documents-to-sign/${doc.id}/sign`,
        { signature_image_b64: dataUrl, consent: true },
        { headers: portalHeaders() },
      );
      if (r.data?.signed) {
        toast.success("Document signé avec succès !");
        onSigned();
        onClose();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de la signature");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="document-sign-modal">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between" style={{ background: `${primaryColor}10` }}>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <FileSignature className="w-5 h-5" style={{ color: primaryColor }} />
            Signer : {doc.template_name}
          </h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700" data-testid="document-sign-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {/* PDF Preview */}
          <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50" style={{ height: "50vh", minHeight: "400px" }}>
            {pdfBlobUrl ? (
              <PdfFrame
                src={`${pdfBlobUrl}#toolbar=1&navpanes=0`}
                openUrl={`${API}/portal/documents-to-sign/${doc.id}/pdf`}
                filename={`${doc.template_name || "document"}.pdf`}
                title="Document à signer"
                height="100%"
                accent={primaryColor}
                testId="document-sign-iframe"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement du document...
              </div>
            )}
          </div>

          {doc.notes && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
              <strong>Note de votre équipe :</strong><br />
              {doc.notes}
            </div>
          )}

          {/* Canvas de signature */}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              Tracez votre signature ci-dessous :
            </label>
            <div className="border-2 border-dashed border-slate-300 rounded-xl bg-white">
              <canvas
                ref={canvasRef}
                width={800}
                height={200}
                className="w-full touch-none cursor-crosshair"
                style={{ height: "180px" }}
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                data-testid="document-sign-canvas"
              />
            </div>
            <div className="flex justify-end mt-2">
              <button
                onClick={clear}
                className="text-xs text-slate-500 hover:text-slate-700 underline"
                data-testid="document-sign-clear-btn"
              >
                Effacer
              </button>
            </div>
          </div>

          {/* Consentement */}
          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer" data-testid="document-consent-label">
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1"
              data-testid="document-consent-checkbox"
            />
            <span>
              Je confirme que la signature tracée ci-dessus est la mienne et qu&apos;elle constitue
              mon consentement légal à ce document <em>(loi RLRQ c C-1.1)</em>.
            </span>
          </label>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50"
            data-testid="document-sign-cancel-btn"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={!hasContent || !consent || submitting}
            className="px-4 py-2 rounded-lg text-white text-sm font-semibold hover:opacity-90 inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: primaryColor }}
            data-testid="document-sign-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {submitting ? "Signature en cours..." : "Valider la signature"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DocumentsToSignTab({ primaryColor = "#072354" }) {
  const [docs, setDocs] = useState([]);           // nouveaux client_documents
  const [legacyMandats, setLegacyMandats] = useState([]);  // anciens mandats Google Docs
  const [fiscalMandates, setFiscalMandates] = useState([]); // Batch #14.236 — formulaires à remplir
  const [taxReports, setTaxReports] = useState([]); // Batch #14.239 — rapports d'impôt à signer
  const [contracts, setContracts] = useState([]); // Batch #14.240 — contrats de services à signer
  const [questionnaires, setQuestionnaires] = useState([]); // Batch #14.242 — questionnaires à remplir + signer
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4, r5, r6] = await Promise.all([
        axios.get(`${API}/portal/documents-to-sign`, { headers: portalHeaders() }),
        // Anciens mandats — best-effort (peut échouer si endpoint absent)
        axios.get(`${API}/portal/signatures/mandats`, { headers: portalHeaders() }).catch(() => ({ data: { items: [] } })),
        // Batch #14.236 — Mandats fiscaux (formulaire à remplir + signer)
        axios.get(`${API}/portal/fiscal-mandates`, { headers: portalHeaders() }).catch(() => ({ data: { items: [] } })),
        // Batch #14.239 — Rapports d'impôt à signer
        axios.get(`${API}/portal/tax-reports`, { headers: portalHeaders() }).catch(() => ({ data: { items: [] } })),
        // Batch #14.240 — Contrats de services à signer
        axios.get(`${API}/portal/service-contracts`, { headers: portalHeaders() }).catch(() => ({ data: { items: [] } })),
        // Batch #14.242 — Questionnaires à remplir + signer
        axios.get(`${API}/portal/questionnaires`, { headers: portalHeaders() }).catch(() => ({ data: { items: [] } })),
      ]);
      setDocs(r1.data?.items || []);
      setLegacyMandats(r2.data?.items || []);
      setFiscalMandates(r3.data?.items || []);
      setTaxReports(r4.data?.items || []);
      setContracts(r5.data?.items || []);
      setQuestionnaires(r6.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (d) => {
    const filename = `${d.template_name || "Document"}-signé.pdf`;
    await secureDownload(`/api/portal/documents-to-sign/${d.id}/pdf`, filename);
  };

  if (loading) {
    return (
      <div className="text-sm text-slate-500 flex items-center gap-2" data-testid="documents-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement de vos documents...
      </div>
    );
  }

  const allEmpty = docs.length === 0 && legacyMandats.length === 0 && fiscalMandates.length === 0 && taxReports.length === 0 && contracts.length === 0 && questionnaires.length === 0;

  if (allEmpty) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center" data-testid="documents-empty">
        <FileSignature className="w-10 h-10 mx-auto text-slate-300 mb-3" />
        <p className="text-sm text-slate-500">
          Aucun document à signer pour le moment.<br />
          Les documents que votre cabinet vous enverra apparaîtront ici.
        </p>
      </div>
    );
  }

  const pendingCount = docs.filter((d) => d.status === "pending").length
    + legacyMandats.filter((m) => m.state === "ready_to_sign").length
    + fiscalMandates.filter((f) => f.status === "pending").length
    + taxReports.filter((t) => t.status === "pending").length
    + contracts.filter((ct) => ct.status === "pending").length
    + questionnaires.filter((qn) => qn.status === "pending").length;

  return (
    <div className="space-y-3" data-testid="documents-list">
      {pendingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-2 text-sm text-amber-900" data-testid="documents-pending-banner">
          <FileSignature className="w-4 h-4" />
          <span><strong>{pendingCount}</strong> document(s) en attente de votre signature</span>
        </div>
      )}

      {/* Nouveaux documents (client_documents) */}
      {docs.map((d) => (
        <div
          key={d.id}
          className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:shadow-md transition"
          data-testid={`document-row-${d.id}`}
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: `${primaryColor}15` }}
          >
            <FileSignature className="w-5 h-5" style={{ color: primaryColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-semibold text-gray-900 text-sm truncate">{d.template_name}</h3>
              <StatusPill status={d.status} />
              {d.status === "signed" && <Lock className="w-3.5 h-3.5 text-emerald-500" title="Document verrouillé" />}
            </div>
            <p className="text-xs text-slate-500">
              Reçu le {formatDate(d.created_at)}
              {d.signature?.signed_at && ` · Signé le ${formatDate(d.signature.signed_at)}`}
            </p>
            {d.notes && (
              <p className="text-xs text-slate-400 italic mt-1 truncate">{d.notes}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            {d.status === "pending" && (
              <button
                onClick={() => setSigning(d)}
                className="px-3 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 inline-flex items-center gap-1.5"
                style={{ background: primaryColor }}
                data-testid={`document-sign-btn-${d.id}`}
              >
                <Pen className="w-4 h-4" /> Signer
              </button>
            )}
            {d.status === "signed" && (
              <button
                onClick={() => handleDownload(d)}
                className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 inline-flex items-center gap-1.5"
                data-testid={`document-download-btn-${d.id}`}
              >
                <Download className="w-4 h-4" /> PDF signé
              </button>
            )}
          </div>
        </div>
      ))}

      {/* Batch #14.236 — Mandats fiscaux (formulaire à remplir + signer) */}
      {fiscalMandates.map((f) => (
        <div
          key={`fiscal-${f.id}`}
          className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:shadow-md transition"
          data-testid={`fiscal-mandate-row-${f.id}`}
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: `${primaryColor}15` }}
          >
            <FileSignature className="w-5 h-5" style={{ color: primaryColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-semibold text-gray-900 text-sm truncate">
                Mandat fiscal {f.tax_year} — Formulaire de renseignements
              </h3>
              <StatusPill status={f.status === "completed" ? "signed" : "pending"} />
            </div>
            <p className="text-xs text-slate-500">
              Reçu le {formatDate(f.created_at)}
              {f.signed_at && ` · Rempli et signé le ${formatDate(f.signed_at)}`}
            </p>
            {f.notes && <p className="text-xs text-slate-400 italic mt-1 truncate">{f.notes}</p>}
          </div>
          {f.status === "pending" && (
            <a
              href={`/mandat-fiscal/${f.token}`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 inline-flex items-center gap-1.5 shrink-0"
              style={{ background: primaryColor }}
              data-testid={`fiscal-mandate-fill-btn-${f.id}`}
            >
              <Pen className="w-4 h-4" /> Remplir et signer
            </a>
          )}
        </div>
      ))}

      {/* Batch #14.239 — Rapports d'impôt à signer */}
      {taxReports.map((t) => (
        <div
          key={`taxrep-${t.id}`}
          className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:shadow-md transition"
          data-testid={`tax-report-row-${t.id}`}
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: `${primaryColor}15` }}
          >
            <FileSignature className="w-5 h-5" style={{ color: primaryColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-semibold text-gray-900 text-sm truncate">
                Rapport d'impôt {t.tax_year} — confirmation de remise
              </h3>
              <StatusPill status={t.status === "completed" ? "signed" : "pending"} />
            </div>
            <p className="text-xs text-slate-500">
              Reçu le {formatDate(t.created_at)}
              {t.signed_at && ` · Signé le ${formatDate(t.signed_at)}`}
            </p>
            {t.notes && <p className="text-xs text-slate-400 italic mt-1 truncate">{t.notes}</p>}
          </div>
          <a
            href={`/rapport-impot/${t.token}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 inline-flex items-center gap-1.5 shrink-0"
            style={{ background: primaryColor }}
            data-testid={`tax-report-open-btn-${t.id}`}
          >
            <Pen className="w-4 h-4" /> {t.status === "pending" ? "Consulter et signer" : "Voir le rapport signé"}
          </a>
        </div>
      ))}

      {/* Batch #14.240 — Contrats de services à signer */}
      {contracts.map((ct) => (
        <div
          key={`contract-${ct.id}`}
          className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:shadow-md transition"
          data-testid={`contract-row-${ct.id}`}
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: `${primaryColor}15` }}
          >
            <FileSignature className="w-5 h-5" style={{ color: primaryColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-semibold text-gray-900 text-sm truncate">
                {ct.title || "Contrat de services"}
              </h3>
              <StatusPill status={ct.status === "completed" ? "signed" : "pending"} />
            </div>
            <p className="text-xs text-slate-500">
              Reçu le {formatDate(ct.created_at)}
              {ct.signed_at && ` · Signé le ${formatDate(ct.signed_at)}`}
            </p>
            {ct.notes && <p className="text-xs text-slate-400 italic mt-1 truncate">{ct.notes}</p>}
          </div>
          <a
            href={`/contrat-service/${ct.token}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 inline-flex items-center gap-1.5 shrink-0"
            style={{ background: primaryColor }}
            data-testid={`contract-open-btn-${ct.id}`}
          >
            <Pen className="w-4 h-4" /> {ct.status === "pending" ? "Consulter et signer" : "Voir le contrat signé"}
          </a>
        </div>
      ))}

      {/* Batch #14.242 — Questionnaires à remplir + signer */}
      {questionnaires.map((qn) => (
        <div
          key={`qn-${qn.id}`}
          className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:shadow-md transition"
          data-testid={`questionnaire-row-${qn.id}`}
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: `${primaryColor}15` }}
          >
            <FileSignature className="w-5 h-5" style={{ color: primaryColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-semibold text-gray-900 text-sm truncate">{qn.title || "Questionnaire"}</h3>
              <StatusPill status={qn.status === "completed" ? "signed" : "pending"} />
            </div>
            <p className="text-xs text-slate-500">
              Reçu le {formatDate(qn.created_at)}
              {qn.signed_at && ` · Rempli et signé le ${formatDate(qn.signed_at)}`}
            </p>
            {qn.notes && <p className="text-xs text-slate-400 italic mt-1 truncate">{qn.notes}</p>}
          </div>
          <a
            href={`/questionnaire/${qn.token}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-2 rounded-lg text-white text-sm font-medium hover:opacity-90 inline-flex items-center gap-1.5 shrink-0"
            style={{ background: primaryColor }}
            data-testid={`questionnaire-open-btn-${qn.id}`}
          >
            <Pen className="w-4 h-4" /> {qn.status === "pending" ? "Remplir et signer" : "Voir le questionnaire signé"}
          </a>
        </div>
      ))}

      {/* Anciens mandats Google Docs (backwards-compat) */}
      {legacyMandats.map((m) => (
        <div
          key={`legacy-${m.id}`}
          className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3 hover:shadow-md transition"
          data-testid={`legacy-mandat-row-${m.id}`}
        >
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: `${primaryColor}15` }}
          >
            <FileSignature className="w-5 h-5" style={{ color: primaryColor }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-900 text-sm truncate">{m.doc_name || "Mandat"}</h3>
              <StatusPill status={m.state} />
              {m.doc_locked && <Lock className="w-3.5 h-3.5 text-emerald-500" />}
              <span className="text-xs text-slate-400">(ancien système)</span>
            </div>
            <p className="text-xs text-slate-500">
              Année {m.tax_year} · Créé le {formatDate(m.created_at)}
            </p>
          </div>
          {m.doc_url && (
            <a
              href={m.doc_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50 inline-flex items-center gap-1.5 shrink-0"
              data-testid={`legacy-mandat-view-${m.id}`}
            >
              <ExternalLink className="w-4 h-4" /> Consulter
            </a>
          )}
        </div>
      ))}

      {signing && (
        <DocumentSignModal
          document={signing}
          onClose={() => setSigning(null)}
          onSigned={() => load()}
          primaryColor={primaryColor}
        />
      )}
    </div>
  );
}
